import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '@config/firebase';
import { useAuthContext } from './AuthProvider';
import { TIER_FEATURES } from '@config/features';
import type {
  Subscription,
  SubscriptionTier,
  FeatureKey,
  LimitKey,
  PilotFeatureLevel,
} from '@/types';

interface FeatureGateResult {
  allowed: boolean;
  reason?: string;
  upgradeUrl?: string;
  remainingCount?: number;
}

interface SubscriptionContextValue {
  subscription: Subscription | null;
  tier: SubscriptionTier;
  loading: boolean;

  // Feature checking
  checkFeature: (feature: FeatureKey) => FeatureGateResult;
  checkLimit: (limitKey: LimitKey, currentCount: number) => FeatureGateResult;
  hasFeature: (feature: FeatureKey) => boolean;
  getLimit: (limitKey: LimitKey) => number;

  // Usage tracking
  usage: {
    aiGradingCount: number;
    aiGradingLimit: number;
    studentsCount: number;
    studentsLimit: number;
  };

  // Admin and pilot state
  isAdmin: boolean;
  isPilot: boolean;
  pilotDaysRemaining: number | null;
  pilotFeatureLevel: PilotFeatureLevel | null;
  showPilotBanner: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  // Determine if user is admin
  const isAdmin = user?.isAdmin === true || user?.role === 'admin';

  // Get the base tier from user (normalize legacy 'professional' stored in Firestore → 'pro')
  const rawTier = user?.subscriptionTier ?? 'free';
  const baseTier: SubscriptionTier = (rawTier as string) === 'professional' ? 'pro' : rawTier;

  // Determine effective tier (admin overrides, pilot resolves to feature level)
  const tier: SubscriptionTier = isAdmin ? 'admin' : baseTier;

  // Pilot-specific state
  const isPilot = tier === 'pilot';
  const pilotConfig = subscription?.pilot;

  // Calculate pilot days remaining
  const pilotDaysRemaining = useMemo(() => {
    if (!pilotConfig?.trialEndDate) return null;
    const now = Timestamp.now();
    const endMs = pilotConfig.trialEndDate.toMillis();
    const nowMs = now.toMillis();
    return Math.ceil((endMs - nowMs) / (1000 * 60 * 60 * 24));
  }, [pilotConfig?.trialEndDate]);

  // Get pilot feature level
  const pilotFeatureLevel: PilotFeatureLevel | null = pilotConfig?.features ?? null;

  // Show pilot banner when trial is ending soon (within 14 days)
  const showPilotBanner = isPilot && pilotDaysRemaining !== null && pilotDaysRemaining <= 14 && pilotDaysRemaining > 0;

  // Resolve effective tier for feature checking
  // Pilots get features based on their assigned level (pro or school)
  const effectiveTier: SubscriptionTier = useMemo(() => {
    if (isAdmin) return 'admin';
    if (tier === 'pilot' && pilotFeatureLevel) {
      return pilotFeatureLevel;
    }
    return tier;
  }, [isAdmin, tier, pilotFeatureLevel]);

  const features = TIER_FEATURES[effectiveTier];

  // Subscribe to subscription document changes
  useEffect(() => {
    if (!user?.subscriptionId) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'subscriptions', user.subscriptionId),
      (doc) => {
        if (doc.exists()) {
          setSubscription(doc.data() as Subscription);
        } else {
          setSubscription(null);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching subscription:', error);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [user?.subscriptionId]);

  const checkFeature = useCallback(
    (feature: FeatureKey): FeatureGateResult => {
      // Admin bypass - always allow
      if (isAdmin) {
        return { allowed: true, reason: 'Admin access' };
      }

      const allowed = features.features[feature] ?? false;

      if (!allowed) {
        const requiredTier = getMinimumTierForFeature(feature);
        return {
          allowed: false,
          reason: `${formatFeatureName(feature)} requires ${requiredTier} plan`,
          upgradeUrl: '/settings/subscription',
        };
      }

      return { allowed: true };
    },
    [features, isAdmin]
  );

  const checkLimit = useCallback(
    (limitKey: LimitKey, currentCount: number): FeatureGateResult => {
      // Admin bypass - unlimited
      if (isAdmin) {
        return { allowed: true, remainingCount: Infinity };
      }

      const limit = features.limits[limitKey];

      if (limit !== Infinity && currentCount >= limit) {
        return {
          allowed: false,
          reason: `You've reached your ${formatLimitName(limitKey)} limit (${limit})`,
          upgradeUrl: '/settings/subscription',
          remainingCount: 0,
        };
      }

      return {
        allowed: true,
        remainingCount: limit === Infinity ? Infinity : limit - currentCount,
      };
    },
    [features, isAdmin]
  );

  const hasFeature = useCallback(
    (feature: FeatureKey): boolean => {
      if (isAdmin) return true;
      return features.features[feature] ?? false;
    },
    [features, isAdmin]
  );

  const getLimit = useCallback(
    (limitKey: LimitKey): number => {
      if (isAdmin) return Infinity;
      return features.limits[limitKey] ?? 0;
    },
    [features, isAdmin]
  );

  const usage = {
    aiGradingCount: subscription?.usage?.aiGradingCount ?? 0,
    aiGradingLimit: features.limits.aiGradingsPerMonth,
    studentsCount: subscription?.usage?.studentsCount ?? 0,
    studentsLimit: features.limits.studentsPerClass,
  };

  const value: SubscriptionContextValue = {
    subscription,
    tier,
    loading,
    checkFeature,
    checkLimit,
    hasFeature,
    getLimit,
    usage,
    isAdmin,
    isPilot,
    pilotDaysRemaining,
    pilotFeatureLevel,
    showPilotBanner,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscriptionContext() {
  const context = useContext(SubscriptionContext);

  if (!context) {
    throw new Error(
      'useSubscriptionContext must be used within a SubscriptionProvider'
    );
  }

  return context;
}

// Helper functions
function getMinimumTierForFeature(feature: FeatureKey): string {
  const tiers: SubscriptionTier[] = ['free', 'pro', 'school'];

  for (const tierKey of tiers) {
    if (TIER_FEATURES[tierKey].features[feature]) {
      return tierKey.charAt(0).toUpperCase() + tierKey.slice(1);
    }
  }

  return 'Pro';
}

function formatFeatureName(feature: FeatureKey): string {
  return feature
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase());
}

function formatLimitName(limit: LimitKey): string {
  const names: Record<LimitKey, string> = {
    classes: 'classes',
    studentsPerClass: 'students per class',
    assessmentsPerMonth: 'assessments per month',
    questionBanks: 'question banks',
    aiGradingsPerMonth: 'AI gradings per month',
  };

  return names[limit] ?? limit;
}
